import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSetState } from '@campaignhub/react-hooks'
import { useSelector } from 'react-redux'

import userTypes from '@enums/userTypes'

import { Box, LoadingModule, TabBar } from '@campaignhub/suit-theme'

import {
  faCalendar, faLayerGroup, faTasks,
} from '@fortawesome/pro-light-svg-icons'

import useReduxAction from '@hooks/useReduxAction'

import portraitServices from '@enums/portraitServices'
import droneServices from '@enums/droneServices'

import useCurrentUser from '@hooks/useCurrentUser'
import useClientResources from '@hooks/useClientResources'
import useCtClientResources from '@hooks/useCtClientResources'
import Assets from './components/Assets'
import Resources from './components/Resources'
import Related from './components/Related'
import ClientResources from './components/ClientResources'
import ClientResourcesCT from './components/ClientResourcesCT'

const defaultState = {
  activeTabBarItemKey: 'assets',
  assetAndResourcesTabs: [],
  selectedIds: [],
  defaultView: true,
}

const AssetsAndResources = (props) => {
  const { disableResourceUpload, hideProof, resourceTypes, serviceJob, showResourcesOnly, workflowUpdated } = props

  const [state, setState] = useSetState(defaultState)
  const { activeTabBarItemKey, assetAndResourcesTabs, selectedIds, defaultView } = state

  const { currentUser } = useCurrentUser()

  const assetIncludes = ['comments', 'file', 'histories', 'file.filePreviewRequests']
  useReduxAction('assets', 'loadAssets', {
    serviceJobs: serviceJob.id,
    includes: assetIncludes.join(','),
  }, [serviceJob.id, workflowUpdated])

  useReduxAction('resources', 'loadResources', {
    campaigns: serviceJob?.campaign?.id,
    includes: 'file.filePreviewRequests',
  }, [serviceJob.id])

  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceGroups } = entities

  const { clientResources, loading: loadingClientResources } = useClientResources(serviceJob.id)
  const { ctClientResources } = useCtClientResources(serviceJob.id)

  useEffect(() => {
    setState({ activeTabBarItemKey: 'assets', defaultView: true })
  }, [])

  useEffect(() => {
    const assetTab = {
      icon: faCalendar,
      key: 'assets',
      title: 'Assets',
      onClick: () => setState({ activeTabBarItemKey: 'assets', defaultView: true }),
    }

    const proofTab = {
      icon: faTasks,
      key: 'proofs',
      title: 'Proofs',
      onClick: () => setState({ activeTabBarItemKey: 'proofs', defaultView: false }),
    }

    const resourcesTab = []
    if (resourceTypes.length > 0) {
      resourcesTab.push({
        icon: faLayerGroup,
        key: 'resources',
        title: serviceGroups[serviceJob.serviceGroupId]?.name === 'Copywriting' ? 'Briefs' : 'Resources',
        onClick: () => setState({ activeTabBarItemKey: 'resources', defaultView: false }),
      })
    }

    resourcesTab.push({
      icon: faLayerGroup,
      key: 'relatedResources',
      title: 'Related',
      onClick: () => setState({ activeTabBarItemKey: 'relatedResources', defaultView: false }),
    })

    if (clientResources && clientResources?.uploadedFiles?.files.length > 0) {
      resourcesTab.push({
        icon: faLayerGroup,
        key: 'clientResources',
        title: 'Client',
        onClick: () => setState({ activeTabBarItemKey: 'clientResources', defaultView: false }),
      })
    }

    if (ctClientResources && Object.keys(ctClientResources)?.filter(x => ctClientResources[x].files.length > 0).length > 0) {
      resourcesTab.push({
        icon: faLayerGroup,
        key: 'ctClientResources',
        title: 'CT Client',
        onClick: () => setState({ activeTabBarItemKey: 'ctClientResources', defaultView: false }),
      })
    }

    const tabs = showResourcesOnly
      ? [
        ...resourcesTab,
        ...(currentUser.userRole?.userTypeId === userTypes?.find(x => x.name === 'OnSite')?.id) ? [proofTab] : [],
      ]
      : [assetTab, ...hideProof ? [] : [proofTab], ...resourcesTab]

    // Any entity load reruns this effect (the entities reducer hands back fresh references), so
    // only the default view may claim the active tab — otherwise a fetch on the tab the user
    // picked, e.g. the Related tab's own, drops them back on Assets.
    setState({
      assetAndResourcesTabs: tabs,
      ...defaultView && { activeTabBarItemKey: showResourcesOnly ? 'resources' : 'assets' },
    })
  }, [showResourcesOnly, hideProof, clientResources, ctClientResources, resourceTypes, defaultView])

  const updateSelectedIds = (updatedIds) => {
    setState({ selectedIds: updatedIds })
  }

  const checkActionStatus = (status) => {
    if (status) {
      if (status === 'zip-resources') {
        setState({ selectedIds: [] })
      }
      setState({ defaultView: false })
    }
  }

  return (
    <Box flexDirection="column">
      <LoadingModule loading={loadingClientResources} />
      {!loadingClientResources && (
        <>
          <TabBar
            items={assetAndResourcesTabs}
            selectedItemKey={activeTabBarItemKey}
            invertColors
          />
          <Box flexDirection="column" marginBottom="large" variant="white">
            {!['ctClientResources', 'clientResources', 'resources', 'relatedResources'].includes(activeTabBarItemKey) && (
              <Assets
                activeTabBarItemKey={activeTabBarItemKey}
                callbacks={{ toggleUpdateSelectedIds: updatedIds => updateSelectedIds(updatedIds) }}
                selectedIds={selectedIds}
                serviceJob={serviceJob}
              />
            )}
            {activeTabBarItemKey === 'resources' && (
              <Resources
                callbacks={{ toggleUpdateSelectedIds: updatedIds => updateSelectedIds(updatedIds), toggleCheckActionStatus: status => checkActionStatus(status) }}
                editable
                disableUpload={disableResourceUpload}
                selectedIds={selectedIds}
                serviceJob={serviceJob}
              />
            )}
            {activeTabBarItemKey === 'relatedResources' && (<Related serviceJob={serviceJob} />)}
            {['clientResources'].includes(activeTabBarItemKey) && (
              <ClientResources
                editable
                resources={clientResources?.uploadedFiles?.files}
                serviceJob={serviceJob}
              />
            )}
            {['ctClientResources'].includes(activeTabBarItemKey) && (
              <ClientResourcesCT
                resources={ctClientResources}
                serviceJob={serviceJob}
                isPortrait={portraitServices.find(x => x.id === serviceJob.service?.id) || droneServices.find(x => x.id === serviceJob.service?.id)}
              />
            )}
          </Box>
        </>
      )}

    </Box>
  )
}

AssetsAndResources.propTypes = {
  disableResourceUpload: PropTypes.bool,
  hideProof: PropTypes.bool,
  resourceTypes: PropTypes.array,
  serviceJob: PropTypes.object,
  showResourcesOnly: PropTypes.bool,
  workflowUpdated: PropTypes.bool,
}

export default AssetsAndResources
